import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import { useRef, useState } from "react";
import { Tabs } from "@template/ui";
import { useFinance } from "./FinanceContext";
import {
  checksum,
  clone,
  diffAgainst,
  dl,
  makeSampleTally,
  now,
  parseTally,
  tallyToXML,
  today,
  validate,
} from "./data/model";
import { CUR_MONTH_IDX, MONTHS, SEED } from "./data/seedData";
import type { StagedVersion } from "./data/types";
import { StatusPill } from "./components/StatusChip";

export function TallyImportPage() {
  const {
    model,
    role,
    has,
    setStaged,
    staged,
    nextVersion,
    logAudit,
    setPage,
    versions,
    setVersions,
    setModel,
    snapshots,
    setLastChange,
  } = useFinance();
  const [over, setOver] = useState(false);
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number; rows: number } | null>(
    null,
  );
  const [err, setErr] = useState("");
  const [month, setMonth] = useState(MONTHS[CUR_MONTH_IDX] ?? "");
  const [remarks, setRemarks] = useState("");
  const [tab, setTab] = useState<"upload" | "history">("upload");
  const inputRef = useRef<HTMLInputElement>(null);
  const isAdmin = has("tally.upload");
  const canRollback = has("version.rollback");

  function handleText(text: string, fname: string, size: number) {
    setErr("");
    try {
      const parsed = parseTally(text, fname.endsWith(".xml") ? "xml" : "json");
      const validation = validate(parsed, model);
      const mIdx = Math.max(0, MONTHS.indexOf(parsed.month || month));
      const diff = diffAgainst(validation.rows, model, mIdx);
      const ver = nextVersion(parsed.month || month);
      const v: StagedVersion = {
        id: "v" + Date.now(),
        ver,
        label: (parsed.month || month) + " upload from Tally",
        month: parsed.month || month,
        mIdx,
        fy: parsed.financialYear || SEED.fiscalYear,
        uploadedBy: role,
        uploadDate: now(),
        status: "Draft",
        checksum: checksum(text),
        size: Math.round(size / 102.4) / 10,
        remarks,
        parsed,
        validation,
        diff,
        summary: validation.summary,
        changeCount: diff.counts.total,
        approvals: { "Finance Head": null, Director: null, CEO: null },
        comments: [],
      };
      setStaged(v);
      setFileInfo({ name: fname, size: v.size, rows: validation.summary.total });
      logAudit(
        "File uploaded",
        `${ver} (${v.month}) — ${validation.summary.total} records, checksum ${v.checksum}`,
      );
    } catch (e) {
      setErr("Parse failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  function onFile(f: File | null | undefined) {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => handleText(String(r.result), f.name, f.size);
    r.readAsText(f);
  }

  function loadSample(t: "json" | "xml") {
    const o = makeSampleTally(model, CUR_MONTH_IDX);
    const text = t === "xml" ? tallyToXML(o) : JSON.stringify(o, null, 2);
    handleText(
      text,
      `tally-${(MONTHS[CUR_MONTH_IDX] ?? "").toLowerCase().replace(" ", "-")}.${t}`,
      text.length,
    );
  }
  function dlSample(t: "json" | "xml") {
    const o = makeSampleTally(model, CUR_MONTH_IDX);
    const text = t === "xml" ? tallyToXML(o) : JSON.stringify(o, null, 2);
    dl(text, `sample-tally-export.${t}`, t === "xml" ? "application/xml" : "application/json");
  }

  function rollback(v: (typeof versions)[number]) {
    const snap = snapshots.find((s) => s.ver === v.ver && s.data);
    if (!snap || !snap.data) {
      window.alert("No restorable snapshot for " + v.ver);
      return;
    }
    setModel(clone(snap.data));
    setLastChange({
      version: v.ver + " (rolled back)",
      by: role,
      date: today(),
      cells: {},
      pages: {},
      counts: { added: 0, modified: 0, unchanged: 0 },
    });
    setVersions((vs) => [
      ...vs,
      {
        ...clone(v),
        id: "rb" + Date.now(),
        ver: v.ver + "-RB",
        label: "Rollback to " + v.ver,
        status: "Rolled Back",
        uploadedBy: role,
        publishedDate: now(),
      },
    ]);
    logAudit("Rollback executed", "Reverted to snapshot of " + v.ver);
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Tally Import &amp; Version Control
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Import Tally ERP exports (JSON / XML) — staged, validated, versioned. Production is never
        overwritten directly.
      </Typography>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as "upload" | "history")}
        items={[
          { value: "upload", label: "Upload" },
          { value: "history", label: "Version History" },
        ]}
      />

      <Box sx={{ mt: 2 }}>
        {tab === "upload" ? (
          <Box>
            {!isAdmin ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your role ({role}) cannot upload Tally data. Finance Head / Director / CEO can.
              </Alert>
            ) : null}
            <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mb: 2 }}>
              {[
                "Upload",
                "Create Version",
                "Parse",
                "Validate",
                "Compare",
                "Review",
                "Approve",
                "Publish",
              ].map((s, i) => (
                <Chip key={s} size="small" variant="outlined" label={`${i + 1}. ${s}`} />
              ))}
            </Stack>
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}
            >
              <Box>
                <Box
                  onClick={() => isAdmin && inputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setOver(true);
                  }}
                  onDragLeave={() => setOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOver(false);
                    if (isAdmin) onFile(e.dataTransfer.files[0]);
                  }}
                  sx={{
                    border: 2,
                    borderStyle: "dashed",
                    borderColor: over ? "primary.main" : "divider",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    cursor: isAdmin ? "pointer" : "default",
                  }}
                >
                  <UploadFileIcon sx={{ fontSize: 38, color: "primary.main" }} />
                  <Typography sx={{ fontWeight: 700, mt: 1 }}>
                    Drag &amp; drop a Tally export
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse · JSON / XML
                  </Typography>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".json,.xml"
                    style={{ display: "none" }}
                    onChange={(e) => onFile(e.target.files?.[0])}
                  />
                </Box>
                {err ? (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    {err}
                  </Alert>
                ) : null}
                <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!isAdmin}
                    onClick={() => loadSample("json")}
                  >
                    Load sample (JSON)
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!isAdmin}
                    onClick={() => loadSample("xml")}
                  >
                    Load sample (XML)
                  </Button>
                  <Button size="small" onClick={() => dlSample("json")}>
                    Download .json
                  </Button>
                  <Button size="small" onClick={() => dlSample("xml")}>
                    Download .xml
                  </Button>
                </Stack>
                <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" href="/finance/sample-tally-export.json" download>
                    Bundled sample (.json)
                  </Button>
                  <Button size="small" href="/finance/sample-tally-export.xml" download>
                    Bundled sample (.xml)
                  </Button>
                </Stack>
              </Box>
              <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Import Metadata
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    label="Financial Year"
                    value={SEED.fiscalYear}
                    slotProps={{ input: { readOnly: true } }}
                    size="small"
                  />
                  <TextField
                    select
                    label="Month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    size="small"
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Import Date"
                    value={today()}
                    slotProps={{ input: { readOnly: true } }}
                    size="small"
                  />
                  <TextField
                    label="Remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    multiline
                    minRows={2}
                    size="small"
                    placeholder="e.g. July actuals upload"
                  />
                </Stack>
                {fileInfo ? (
                  <Alert severity="success" sx={{ mt: 1.5 }}>
                    {fileInfo.name} · {fileInfo.size} KB · {fileInfo.rows} records staged
                  </Alert>
                ) : null}
              </Box>
            </Box>
            {staged && staged.status === "Draft" ? (
              <ValidationSummary v={staged} onReview={() => setPage("approvals")} />
            ) : null}
          </Box>
        ) : (
          <Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Month</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Recs</TableCell>
                    <TableCell align="right">Chg</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Checksum</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...versions].reverse().map((v) => (
                    <TableRow key={v.id}>
                      <TableCell sx={{ fontWeight: 700 }}>{v.ver}</TableCell>
                      <TableCell>{v.label}</TableCell>
                      <TableCell>{v.month}</TableCell>
                      <TableCell>{v.uploadedBy}</TableCell>
                      <TableCell>{v.publishedDate || v.uploadDate}</TableCell>
                      <TableCell align="right">{v.summary?.total ?? "—"}</TableCell>
                      <TableCell align="right">{v.changeCount || 0}</TableCell>
                      <TableCell>
                        <StatusPill status={v.status} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{v.checksum}</TableCell>
                      <TableCell>
                        {v.status === "Published" && v.id !== "v0" ? (
                          <Button size="small" disabled={!canRollback} onClick={() => rollback(v)}>
                            Rollback
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Nothing deleted — every version retained (soft delete). Rollback: Director / CEO only.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ValidationSummary({ v, onReview }: { v: StagedVersion; onReview: () => void }) {
  const s = v.validation.summary;
  const [showErr, setShowErr] = useState(true);
  const cards: [string, number, "b" | "g" | "a" | "r"][] = [
    ["Total", s.total, "b"],
    ["Valid", s.valid, "g"],
    ["Warnings", s.warning, "a"],
    ["Errors", s.error, "r"],
    ["Imported", s.imported, "g"],
    ["Skipped", s.skipped, "a"],
  ];
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Validation Summary · {v.ver} · {v.month}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
          gap: 1.5,
          mb: 2,
        }}
      >
        {cards.map(([label, value, tone]) => (
          <Box key={label} sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color:
                  tone === "r"
                    ? "error.main"
                    : tone === "a"
                      ? "warning.main"
                      : tone === "g"
                        ? "success.main"
                        : undefined,
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      {v.validation.errors.length > 0 ? (
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Validation Findings ({v.validation.errors.length})
            </Typography>
            <Button size="small" onClick={() => setShowErr(!showErr)}>
              {showErr ? "Hide" : "Show"}
            </Button>
          </Stack>
          {showErr ? (
            <TableContainer sx={{ maxHeight: 260, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Line</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Rule</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell align="right">Severity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {v.validation.errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{e.line}</TableCell>
                      <TableCell>{e.code}</TableCell>
                      <TableCell>{e.rule}</TableCell>
                      <TableCell>{e.message}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          color={e.severity === "error" ? "error" : "warning"}
                          label={e.severity}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Box>
      ) : null}
      <Alert severity="info" action={<Button onClick={onReview}>Send to Review →</Button>}>
        Error rows are skipped; valid &amp; warning rows proceed. {v.diff.counts.total} data changes
        vs approved version.
      </Alert>
    </Box>
  );
}
